import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User as UserIcon } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface HelpChatbotProps {
  isOpen: boolean;
  onClose: () => void;
}

function getPatternMatchingResponse(userMessage: string): string {
  const lowerMessage = userMessage.toLowerCase();

  if (lowerMessage.includes('connect') && (lowerMessage.includes('microsoft') || lowerMessage.includes('365'))) {
    return "To connect your Microsoft 365 account:\n\n1. Navigate to the Integrations page\n2. Click on the Microsoft 365 card\n3. Follow the OAuth 2.0 authorization flow\n4. Grant Niwt access to your emails\n\nThe process is secure and your data is encrypted.";
  }

  if (lowerMessage.includes('data') || lowerMessage.includes('privacy') || lowerMessage.includes('secure')) {
    return "Your data security is our top priority:\n\n\u2022 SOC 2 Type II certified and CMMC-ready\n\u2022 All data encrypted in transit and at rest\n\u2022 OAuth 2.0 for secure authentication\n\u2022 Your data is never shared or sold\n\nFor more details, contact support@niwt.com";
  }

  if (lowerMessage.includes('digital twin') || lowerMessage.includes('niwt')) {
    return "Digital twins (Niwts) are created when you connect an integration and select employees. The AI learns from:\n\n\u2022 Communication patterns\n\u2022 Writing style and tone\n\u2022 Domain expertise and knowledge\n\nYou can manage employees through the Employee Selection page.";
  }

  if (lowerMessage.includes('disconnect') || lowerMessage.includes('remove')) {
    return "To disconnect an integration:\n\n1. Go to the Integrations page\n2. Find the connected service\n3. Click 'Disconnect'\n\nPreviously ingested data will remain unless you request deletion.";
  }

  if (lowerMessage.includes('employee') || lowerMessage.includes('select')) {
    return "To select employees for AI analysis:\n\n1. Connect your Microsoft 365 account first\n2. Go to Employee Selection from Integrations\n3. Choose up to 10 employees from your directory\n4. Click 'Create Niwts & Start Ingestion'\n\nThe system will process their email data to build AI profiles.";
  }

  if (lowerMessage.includes('ingest') || lowerMessage.includes('process')) {
    return "Email ingestion works in the background:\n\n1. Select employees from your O365 directory\n2. The system fetches and processes their emails\n3. Content is chunked, embedded, and indexed\n4. You can monitor progress on the Ingestion Status page\n\nIngestion typically takes a few minutes per employee.";
  }

  if (lowerMessage.includes('help') || lowerMessage.includes('support') || lowerMessage.includes('contact')) {
    return "I can help you with:\n\n\u2022 Integrations (Microsoft 365)\n\u2022 Employee selection and management\n\u2022 Security and data privacy\n\u2022 Digital twin features\n\u2022 Account management\n\nEmail support@niwt.com for direct assistance.";
  }

  return "I can help you with:\n\n\u2022 Integrations (Microsoft 365)\n\u2022 Employee selection and ingestion\n\u2022 Security and data privacy\n\u2022 Digital twin features\n\u2022 Account management and billing\n\nWhat would you like to know more about?";
}

const HelpChatbot: React.FC<HelpChatbotProps> = ({ isOpen, onClose }) => {
  const initialMessage: Message = {
    id: '1',
    role: 'assistant',
    content: "Hi! I'm the Niwt Help Assistant. I can help you with questions about integrations, security, billing, and getting started with the platform. What can I help you with today?",
    timestamp: new Date()
  };

  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Reset conversation history when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([{
        id: Date.now().toString(),
        role: 'assistant',
        content: "Hi! I'm the Niwt Help Assistant. I can help you with questions about integrations, security, billing, and getting started with the platform. What can I help you with today?",
        timestamp: new Date()
      }]);
      setInputValue('');
      setIsTyping(false);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    const currentInput = inputValue;
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate brief thinking delay then return pattern-matched response
    await new Promise(resolve => setTimeout(resolve, 500));

    const aiResponseText = getPatternMatchingResponse(currentInput);

    const aiResponse: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: aiResponseText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, aiResponse]);
    setIsTyping(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-niwt-primary rounded-full flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-niwt-text">Help Assistant</h2>
              <p className="text-sm text-gray-500">Ask me anything about Niwt</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start space-x-3 ${
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.role === 'assistant' ? 'bg-purple-100' : 'bg-niwt-primary'
              }`}>
                {message.role === 'assistant' ? (
                  <Bot className="w-5 h-5 text-niwt-primary" />
                ) : (
                  <UserIcon className="w-5 h-5 text-white" />
                )}
              </div>
              <div
                className={`max-w-lg px-4 py-3 rounded-2xl ${
                  message.role === 'user'
                    ? 'bg-niwt-primary text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-niwt-primary" />
              </div>
              <div className="bg-gray-100 px-4 py-3 rounded-2xl">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex items-end space-x-3">
            <div className="flex-1">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about Niwt..."
                rows={1}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent resize-none"
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              className="px-6 py-3 bg-niwt-primary text-white rounded-lg hover:bg-niwt-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center">
            Need more help? Email us at <a href="mailto:support@niwt.com" className="text-niwt-primary hover:underline">support@niwt.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpChatbot;
